/* arbt (BA200X family) — one PDP, several products, no page load.
 *
 * Reads the server-rendered [data-arbt-family-data] records (arb-main-product
 * builds them; the contract is in internal/goals/ba200x-family/W2-log.md §W2a)
 * and moves DOM only. Every money string, stock string and button label in
 * those records was already produced by Liquid with money_with_currency and
 * the theme's own translations, so nothing here formats money, and there is
 * no English literal anywhere in this file.
 *
 * Nothing runs unless the page carries a family with two or more members, so
 * every other PDP is untouched by design rather than by a guard.
 *
 * Deliberately NOT done here:
 *   - no fetch of any kind (PLAN §2: the swap is a DOM toggle, not a round trip)
 *   - #stamped-main-widget is never read, moved or re-rendered; its
 *     capture-phase Tab trap (AGENTS.md, Accessibility) has to survive a swap
 *   - no pushState — replaceState only, so the back button still leaves the PDP
 *   - no positive tabindex. Focus is moved in exactly one place: a click on a
 *     Kits & Bundles member link, which replaces a navigation, so focus has to
 *     land somewhere — it goes to that member's own pill (T10)
 */
(function () {
  'use strict';

  var STATES = ['in', 'low', 'out', 'oversell'];

  /* arbt (BA200X family, per-member content bands): video, Clever Bits and
     Features & Benefits (sections/arb-product-overview-video.liquid,
     arb-clever-bits.liquid, arb-product-story-panels.liquid) are each a
     single, un-namespaced shopify-section below the buy box — unlike the
     galleries, tabs and extras that already swap, there is only ONE copy of
     each on the page, and every byte of it is self's. Owner's rule:
     "content from each kit must update when it is selected." A member
     switch re-fetches these three sections, rendered AT THE MEMBER'S OWN
     product URL, through the same Section Rendering API
     sections/arb-main-product.liquid's swapVerticalSections already uses for
     a vertical change — one request, one cache entry, one staleness guard,
     the same shape — but keyed on the MEMBER's product handle rather than a
     vertical's collection, because this is "a different product", not "a
     different route for the same product". See swapMemberSections() below. */
  var MEMBER_SWAP_SECTION_CLASSES = [
    'shopify-section--arb-product-overview-video',
    'shopify-section--arb-clever-bits',
    'shopify-section--arb-product-story-panels',
  ];

  function boot() {
    var dataEl = document.querySelector('[data-arbt-family-data]');
    var root = document.querySelector('[data-arbt-family-switcher]');
    if (!dataEl || !root) return;

    var members;
    try {
      members = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(members) || members.length < 2) return;

    var byHandle = {};
    members.forEach(function (member) {
      if (member && member.handle) byHandle[member.handle] = member;
    });
    /* arbt (W8 E1): the Landscaping slot resolver picks ONE of two products
       to occupy a pill and only ever embeds the winner — a stale ?member=
       or tile link built for the loser (a bookmark from before a swap, or a
       market where the rule flips it) still has to land on the same pill
       rather than 404 into byHandle. The embed carries that loser's handle
       as `aliases` on the winner's own record; second pass so a real handle
       already claimed above is never shadowed by an alias. */
    members.forEach(function (member) {
      if (!member || !member.aliases) return;
      member.aliases.forEach(function (alias) {
        if (alias && !byHandle[alias]) byHandle[alias] = member;
      });
    });
    var self = members[0];

    /* arbt (W8 E4): byHandle already resolves an alias (a losing slot-mate's
       handle) to the WINNING member's object, but the winner's pill is only
       ever rendered under its OWN handle — nothing renders a pill for the
       loser. Anything that reads a handle back out to find or compare a
       PILL (as opposed to just looking up a member record) has to resolve
       through this first, or the pill and the content it controls disagree.
       One helper, used by both the ?member= preselect and the tile-link
       click path below, so neither can drift from the other. */
    function resolveMemberHandle(handle) {
      var member = byHandle[handle];
      return member ? member.handle : handle;
    }

    /* The switcher ships with links visible and radios hidden, so a shopper
       without JS gets a control that works instead of an inert widget. This is
       the moment that swaps which of the two is live. */
    var group = root.querySelector('[data-arbt-family-group]');
    var links = root.querySelector('[data-arbt-family-links]');
    var live = root.querySelector('[data-arbt-family-live]');
    if (!group) return;
    group.hidden = false;
    if (links) links.hidden = true;

    var sectionRoot = root.closest('.shopify-section') || document;
    var titleEl = sectionRoot.querySelector('.arb-buy-box h1');
    var priceEl = sectionRoot.querySelector('[data-arb-price-display]');
    var compareEl = sectionRoot.querySelector('[data-arb-compare-display]');
    var descEl = sectionRoot.querySelector('[data-arb-product-description]');
    /* SELF's description on screen is NOT product.description: when the
       product carries data-allow-vertical-description, arb-context-vertical
       server-renders the vertical-adapted copy (and the ?vertical= script may
       have adapted it again a moment ago — this file boots after it). The
       embed's descriptionHtml is the native field, so repainting self from it
       would quietly drop the vertical copy on the way back. Keep what the
       server put there and restore exactly that for self; members, which have
       no server-rendered copy on this page, come from the embed. */
    var descSelfHtml = descEl ? descEl.innerHTML : '';
    /* The order summary's primary line is labelled from an ATTRIBUTE the
       panel carries (data-arbt-main-title, rendered with the section's own
       product). Once the base price follows the active member, the label has
       to as well, or the panel prices the kit under the skin's name. */
    var summaryEl = sectionRoot.querySelector('[data-arbt-addons-subtotal]');
    var stockEl = sectionRoot.querySelector('[data-arb-stock-indicator]');
    var stockLbl = stockEl && stockEl.querySelector('[data-arb-stock-label]');
    var skuEl = sectionRoot.querySelector('[data-arb-spec-item-code]');
    var voltEl = sectionRoot.querySelector('[data-arb-spec-voltage]');
    var addBtn = document.querySelector('[data-arb-add-to-cart]');
    var barBtn = document.querySelector('[data-arb-buy-bar-add]');
    var barTitle = document.querySelector('.arb-buy-bar__title');
    var breadcrumb = document.querySelector('[data-arb-product-breadcrumb]');
    var breadcrumbCurrent = breadcrumb && breadcrumb.querySelector('.arb-breadcrumbs__current');
    var breadcrumbVertical = breadcrumb && breadcrumb.querySelector('[data-arb-product-breadcrumb-vertical]');
    var breadcrumbVerticalInitial = breadcrumbVertical
      ? { text: breadcrumbVertical.textContent, href: breadcrumbVertical.getAttribute('href') }
      : null;
    var soldOutSuffix = root.getAttribute('data-arbt-family-sold-out-suffix') || '';

    /* ── variant layer ─────────────────────────────────────────────────────
       arbt-option-picker's inline script caches SELF's variant blob in a
       closure and resolves every pill change against it. On a family PDP that
       is the wrong source the moment a kit is active — and worse than wrong:
       the kit shares the parent's Voltage values, so the legacy resolver would
       happily write a SKIN variant id into the form while a kit is on screen.
       Replacing the picker node with a clone drops that listener (listeners do
       not clone) and leaves the markup, the checked state and the hidden
       input[name="id"] — form attribute and all — exactly as rendered. From
       here this file is the only thing that resolves a variant on this page.  */
    var picker = sectionRoot.querySelector('[data-arbt-option-picker]');
    if (picker) {
      var pickerClone = picker.cloneNode(true);
      picker.parentNode.replaceChild(pickerClone, picker);
      picker = pickerClone;
    }

    var form = null;
    if (picker) {
      var formId = picker.getAttribute('data-arbt-picker-form');
      if (formId) form = document.getElementById(formId);
    }
    if (!form) {
      var anyId = document.querySelector('input[name="id"]');
      form = anyId && anyId.form;
    }

    function idInputs() {
      return Array.prototype.slice.call(document.querySelectorAll('input[name="id"]')).filter(function (el) {
        return !form || el.form === form;
      });
    }

    /* Labels for the pills come from what the server already rendered for
       self — arbt-option-label produced them, translated, with the region
       suffix rule applied. A value only self lacks falls back to the member's
       own `voltage` string, which is the same filter's output, and is exact
       whenever the member has a single option dimension (this family does).
       Raw value last, so an unlabelled dimension still renders something. */
    var labelByValue = {};
    if (picker) {
      Array.prototype.slice.call(picker.querySelectorAll('input[type="radio"][data-arbt-option-index]')).forEach(function (radio) {
        var label = radio.closest('label');
        if (!label) return;
        var text = (label.textContent || '').trim();
        if (soldOutSuffix) text = text.replace(' (' + soldOutSuffix + ')', '').trim();
        if (text) labelByValue[radio.value] = text;
      });
    }

    function labelFor(member, index, value) {
      if (labelByValue[value]) return labelByValue[value];
      for (var i = 0; i < member.variants.length; i++) {
        var v = member.variants[i];
        if (v.options && v.options.length === 1 && v.options[0] === value && v.voltage) return v.voltage;
      }
      return value;
    }

    /* One pill template, cloned — so the family picker cannot drift from the
       option picker's markup even if that markup changes. The radio NAME per
       dimension is read once, here: a member that collapses a dimension to one
       value empties that group, and the name would be gone by the time a
       member that still has two values needs it back. */
    var pillTemplate = picker && picker.querySelector('label');
    var groupNames = {};
    if (picker) {
      Array.prototype.slice.call(picker.querySelectorAll('[data-arbt-option-group]')).forEach(function (groupEl) {
        var first = groupEl.querySelector('input[type="radio"]');
        if (first) groupNames[groupEl.getAttribute('data-arbt-option-group')] = first.getAttribute('name');
      });
    }

    function rebuildPicker(member) {
      if (!picker || !pillTemplate) return;
      Array.prototype.slice.call(picker.querySelectorAll('[data-arbt-option-group]')).forEach(function (groupEl) {
        var index = Number(groupEl.getAttribute('data-arbt-option-group'));
        var wrap = groupEl.querySelector('[role="radiogroup"]');
        if (!wrap || isNaN(index)) return;

        var seen = {};
        var values = [];
        member.variants.forEach(function (v) {
          var value = v.options && v.options[index];
          if (value === undefined || value === null || value === '') return;
          if (seen[value]) return;
          seen[value] = { available: false };
          values.push(value);
        });
        member.variants.forEach(function (v) {
          var value = v.options && v.options[index];
          if (value && seen[value] && v.available) seen[value].available = true;
        });

        /* Same rule the server uses: a dimension with one surviving value is
           nothing to choose, so it is not shown. */
        if (values.length < 2) {
          groupEl.hidden = true;
          wrap.textContent = '';
          return;
        }
        groupEl.hidden = false;

        var name = groupNames[String(index)] || null;
        wrap.textContent = '';

        var current = currentVariant(member);
        values.forEach(function (value) {
          var pill = pillTemplate.cloneNode(true);
          var input = pill.querySelector('input[type="radio"]');
          if (!input) return;
          input.value = value;
          input.setAttribute('data-arbt-option-index', String(index));
          if (name) input.setAttribute('name', name);
          input.checked = !!(current && current.options && current.options[index] === value);
          /* Replace the label's text without touching the input node. */
          Array.prototype.slice.call(pill.childNodes).forEach(function (node) {
            if (node.nodeType === 3) pill.removeChild(node);
          });
          var text = labelFor(member, index, value);
          if (!seen[value].available && soldOutSuffix) text = text + ' (' + soldOutSuffix + ')';
          pill.appendChild(document.createTextNode(text));
          wrap.appendChild(pill);
        });
      });
    }

    var activeHandle = self.handle;
    var activeVariantId = {};

    function currentVariant(member) {
      var wanted = activeVariantId[member.handle] || member.variantId;
      var fallback = null;
      for (var i = 0; i < member.variants.length; i++) {
        if (!fallback) fallback = member.variants[i];
        if (String(member.variants[i].id) === String(wanted)) return member.variants[i];
      }
      return fallback;
    }

    function setButton(btn, available, label) {
      if (!btn) return;
      btn.disabled = !available;
      if (available) btn.removeAttribute('aria-disabled');
      else btn.setAttribute('aria-disabled', 'true');
      if (!label) return;
      for (var i = 0; i < btn.childNodes.length; i++) {
        if (btn.childNodes[i].nodeType === 3 && btn.childNodes[i].textContent.trim()) {
          btn.childNodes[i].textContent = ' ' + label + ' ';
          return;
        }
      }
    }

    /* The gallery instances are all server-rendered, one per member, all but
       one carrying `hidden`. P7 measured that a revealed instance measures and
       indexes correctly with no rAF and no timeout, so this is a straight
       attribute toggle. */
    function showGallery(member) {
      var galleries = document.querySelectorAll('[data-arb-gallery][data-arbt-family-member]');
      Array.prototype.forEach.call(galleries, function (gallery) {
        gallery.hidden = gallery.getAttribute('data-arbt-family-member') !== member.handle;
      });
      return document.querySelector('[data-arb-gallery][data-arbt-family-member="' + cssEscape(member.handle) + '"]');
    }

    function cssEscape(value) {
      return String(value).replace(/["\\]/g, '\\$&');
    }

    /* Ruling R12: kits are single-vertical, so the vertical block belongs to
       the MEMBER, not to the route. arb-main-product server-renders one block
       per member (each resolved through arb-context-vertical with
       ignore_collection: true on that member's own product), all but self
       carrying `hidden` — so this is the same attribute toggle the galleries
       get, and no vertical vocabulary lives in this file. Self's block keeps
       the route-resolved vertical and the ?vertical= script keeps working on
       it, because it is the first one in document order. */
    function showVerticalBlock(member) {
      var blocks = document.querySelectorAll('[data-arbt-family-vertical-block][data-arbt-family-member]');
      Array.prototype.forEach.call(blocks, function (block) {
        block.hidden = block.getAttribute('data-arbt-family-member') !== member.handle;
      });
    }

    /* The tabs widget (What's Included / Specifications / Support) is the same
       shape as the galleries: sections/arb-product-tabs.liquid server-renders
       one per member, all but the active one `hidden`, each namespaced by its
       own tabs id so no two inputs, labels or panels share an id. It lives in
       its own shopify-section, so the lookup is document-wide — the one place
       in this file that is deliberately not scoped to sectionRoot.

       The widget is a CSS-only radio group, so "which tab is open" is the
       checked radio, and a hidden widget keeps its own. Carry the tab the
       shopper is reading across the swap when the incoming member has that
       tab (its id is `<tabs id>-<key>`, and the key is read off the wrapper's
       own tabs id, so no tab name is spelled out here), and fall back to that
       member's first tab when it does not — a kit with only What's Included
       opens on it instead of opening on nothing.

       A member whose three sources are all empty, or whose
       arbortech.show_product_tabs is off, has NO widget: nothing matches, every
       widget ends up hidden, and the tabs area is simply absent while that
       member is active. That is the same omission rule the section already
       applies per product. */
    function tabKey(input, tabsId) {
      var id = input.id || '';
      if (!tabsId || id.indexOf(tabsId + '-') !== 0) return '';
      return id.slice(tabsId.length + 1);
    }

    function showTabs(member) {
      var widgets = document.querySelectorAll('[data-arbt-family-tabs][data-arbt-family-member]');
      if (!widgets.length) return;

      var openKey = '';
      Array.prototype.forEach.call(widgets, function (widget) {
        if (widget.hidden || openKey) return;
        var checked = widget.querySelector('input[type="radio"]:checked');
        if (checked) openKey = tabKey(checked, widget.getAttribute('data-arbt-family-tabs-id'));
      });

      Array.prototype.forEach.call(widgets, function (widget) {
        var mine = widget.getAttribute('data-arbt-family-member') === member.handle;
        widget.hidden = !mine;
        if (!mine) return;
        var inputs = widget.querySelectorAll('input[type="radio"]');
        if (!inputs.length) return;
        var tabsId = widget.getAttribute('data-arbt-family-tabs-id');
        var wanted = null;
        Array.prototype.forEach.call(inputs, function (input) {
          if (!wanted && openKey && tabKey(input, tabsId) === openKey) wanted = input;
        });
        (wanted || inputs[0]).checked = true;
      });
    }

    /* The extras — "Configure your order" accessories and the Kits & bundles
       tiles — are the same per-member wrapper shape as the galleries, the
       vertical blocks and the tabs: sections/arb-main-product.liquid renders
       one block per member (each accessory list resolved from that member's
       OWN vertical, each tile panel already minus that member), all but the
       active one `hidden`, every input id namespaced by the member's handle so
       two members offering the same accessory cannot collide.

       Unticking the outgoing member's boxes is not tidiness: a checked input
       inside a hidden block is still :checked, so both order summaries would
       keep counting it and the submit handler would still post it — the
       shopper would be sold the masonry kit's accessories with the landscaping
       kit. One bubbling change event is enough, because every listener
       recomputes from the whole section rather than from the row that fired. */
    function showExtras(member) {
      var blocks = document.querySelectorAll('[data-arbt-family-extras][data-arbt-family-member]');
      if (!blocks.length) return;
      var lastCleared = null;
      Array.prototype.forEach.call(blocks, function (block) {
        var mine = block.getAttribute('data-arbt-family-member') === member.handle;
        block.hidden = !mine;
        if (mine) return;
        Array.prototype.forEach.call(block.querySelectorAll('input[type="checkbox"]:checked'), function (input) {
          input.checked = false;
          lastCleared = input;
        });
      });
      if (lastCleared) lastCleared.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* MEMBER_SWAP_SECTION_CLASSES, captured NOW — before any swap has ever
       run — so `initial` is always the true server render, whichever member
       apply() happens to be called with first (a pill click after full load,
       or the ?member= preselect at the bottom of boot()). This runs
       synchronously during boot(), strictly before that preselect call, so
       it is never raced by it. */
    var memberSwapTargets = MEMBER_SWAP_SECTION_CLASSES
      .map(function (className) { return document.querySelector('.' + className); })
      .filter(Boolean)
      .map(function (element) {
        return {
          element: element,
          sectionId: element.id.replace(/^shopify-section-/, ''),
          initial: element.innerHTML,
        };
      });
    var memberSectionCache = {};
    var memberSectionRequest = 0;
    var memberSectionAbort = null;

    /* `?member=` (memberUrl(), below) only ever rewrites the query string —
       window.location.pathname always still ends with SELF's own handle, so
       swapping just that last /products/<handle> segment keeps whatever
       precedes it (locale root, collection route) exactly as the shopper
       arrived. That is "the route's collection prefix preserved" in
       practice: /collections/<c>/products/self -> /collections/<c>/products/
       <member>, /de-de/products/self -> /de-de/products/<member>. */
    function memberSectionsUrl(handle) {
      var match = window.location.pathname.match(/^(.*\/products\/)[^/]+\/?$/);
      return match ? match[1] + handle : null;
    }

    /* Freshly fetched markup is assigned via innerHTML, so any <script> tag
       it carries never runs — this dispatches the SAME reinit signal the
       Shopify theme editor sends after its own section reloads, which every
       listener that already cares is already wired for: arb-projects-grid.js
       (the video's modal + focus trap), arb-content-reveal-scroll.js (the
       panels' scroll-in, reduced-motion guarded exactly as today),
       arb-button-038.js and arb-product-story-panels.js (CTA link upgrade).
       Nothing new has to be taught to any of those files.
       <arb-product-overview-video> re-upgrades on its own the moment it is
       parsed into the document — true here because self also carries a
       video, so customElements.define already ran on this page's first
       paint. */
    function dispatchSectionLoad(target) {
      target.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    }

    function applyMemberSectionsHtml(sections) {
      memberSwapTargets.forEach(function (target) {
        var html = sections && sections[target.sectionId];
        var fresh = null;
        if (html) {
          var temp = document.createElement('div');
          temp.innerHTML = html;
          fresh = temp.querySelector('#' + CSS.escape(target.element.id)) || temp.firstElementChild;
        }
        /* A kit with no video, or no panels, or no Clever Bits: each
           section's own Liquid already renders nothing but its (invisible)
           <link>/<script> tags when its content is blank — the identical
           collapse a bare vertical_product_content entry already gets on
           every other PDP. There is no separate empty state to build, and
           nothing to measure beyond what those sections already do. */
        target.element.innerHTML = fresh ? fresh.innerHTML : '';
        dispatchSectionLoad(target.element);
      });
    }

    function swapMemberSections(member) {
      if (!memberSwapTargets.length) return;
      var requestId = ++memberSectionRequest;

      /* swapVerticalSections (arb-main-product.liquid) targets two of these
         same three sections, at a different URL, for a ?vertical= override
         that happens to coincide with a member selection. A member always
         wins: this marks any vertical fetch already in flight stale through
         the SAME guard that function already checks internally —
         swapVerticalSections' own code and behaviour for an actual vertical
         change are untouched by this. */
      if (typeof window.arbtInvalidateVerticalSectionFetch === 'function') {
        window.arbtInvalidateVerticalSectionFetch();
      }
      if (memberSectionAbort) {
        memberSectionAbort.abort();
        memberSectionAbort = null;
      }

      if (member.handle === self.handle) {
        memberSwapTargets.forEach(function (target) {
          target.element.innerHTML = target.initial;
          dispatchSectionLoad(target.element);
        });
        return;
      }

      if (memberSectionCache[member.handle] !== undefined) {
        applyMemberSectionsHtml(memberSectionCache[member.handle]);
        return;
      }

      var url = memberSectionsUrl(member.handle);
      if (!url) return;
      var sectionList = memberSwapTargets.map(function (target) { return target.sectionId; }).join(',');
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      memberSectionAbort = controller;

      fetch(url + '?sections=' + encodeURIComponent(sectionList), controller ? { signal: controller.signal } : undefined)
        .then(function (response) {
          return response.ok ? response.json() : Promise.reject(new Error(String(response.status)));
        })
        .then(function (data) {
          if (requestId !== memberSectionRequest) return; /* stale */
          memberSectionCache[member.handle] = data || {};
          applyMemberSectionsHtml(memberSectionCache[member.handle]);
        })
        .catch(function () {}); /* aborted, or failed — keep current content */
    }

    /* The member is a query param on the URL the shopper is ALREADY on, never
       a rebuilt product URL: `?view=arbt-family` (how the family template is
       previewed before the parent's templateSuffix is set) and `?vertical=`
       have to survive a swap, and so does anything a campaign added. Only
       `setup` is dropped — it is the retired parent's deep link, and `member`
       is the same statement in the current spelling, so leaving both would
       publish a URL that could contradict itself. */
    function memberUrl(member) {
      var params = new URLSearchParams(window.location.search);
      params.delete('setup');
      params.set('member', member.handle);
      var query = params.toString();
      return window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    }

    /* initVariantMedia (assets/arb-product-gallery.js) caches SELF's variant
       blob at boot, so on a member gallery it finds no record and no-ops. The
       jump for the active member is therefore owned here — same rule it uses:
       real slides only, and stay put when the variant's photo is not one.
       Returns true when it moved the carousel, so the caller knows whether the
       re-seat below still has to run. */
    function jumpToVariantMedia(gallery, variant) {
      if (!gallery || !variant || !variant.mediaId) return false;
      var carousel = gallery.querySelector('blossom-carousel');
      if (!carousel || typeof carousel.goToIndex !== 'function') return false;
      var slides = gallery.querySelectorAll('[data-blossom-slide][data-arb-media-id]');
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-arb-media-id') === String(variant.mediaId)) {
          carousel.goToIndex(i);
          return true;
        }
      }
      return false;
    }

    /* T12 (client QA 2026-09-18): a carousel that was `hidden` when
       blossom-carousel built its loop clones comes back parked at scrollLeft 0
       — and 0 is the LEAD CLONE, a duplicate of the member's LAST photo, not
       its first slide (the real slides start one slide-width in). Its
       data-active-index and its thumb strip meanwhile still read 0, so the
       stage showed the kit's last image while the FIRST thumb was marked
       active: the reported "the thumbs don't follow". Measured on the real
       3-member family — revealed instances sat at scrollLeft 0 with the first
       real slide at offsetLeft 620.

       goToIndex() re-seats the scroll AND is what the vendor's blossom-dots
       listens to, so ONE call fixes the stage and the thumbs together — there
       is no second mechanism to add and nothing to re-measure (refresh() also
       recovers it, and is not needed once this runs). Two measured properties
       make it work: it does NOT short-circuit on an index the carousel already
       believes is active, which is what makes index 0 recoverable, and
       is-active/aria-current/roving tabindex on the dots all follow it.

       The target is the carousel's OWN active index, not a fixed 0: on a first
       reveal that IS the server's data-initial-index (the member's
       featured/variant media), and on a return visit it keeps the slide the
       shopper had chosen inside that member's gallery. A variant match still
       wins over both. */
    function seatCarousel(gallery) {
      if (!gallery) return;
      var carousel = gallery.querySelector('blossom-carousel');
      if (!carousel || typeof carousel.goToIndex !== 'function') return;
      var index = parseInt(carousel.getAttribute('data-active-index'), 10);
      if (isNaN(index) || index < 0) index = parseInt(carousel.getAttribute('data-initial-index'), 10);
      if (isNaN(index) || index < 0) index = 0;
      carousel.goToIndex(index);
    }

    function showGalleryMedia(gallery, variant) {
      if (!jumpToVariantMedia(gallery, variant)) seatCarousel(gallery);
    }

    function paint(member, variant) {
      if (titleEl) titleEl.textContent = member.title;
      if (barTitle) barTitle.textContent = member.title;
      if (breadcrumbCurrent) breadcrumbCurrent.textContent = member.title;

      var priceHtml = (variant && variant.priceHtml) || member.priceHtml;
      var compareHtml = variant ? variant.compareHtml : member.compareHtml;
      if (priceEl) priceEl.textContent = priceHtml;
      if (compareEl) {
        if (compareHtml) {
          compareEl.textContent = compareHtml;
          compareEl.classList.remove('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('hidden');
        }
      }

      if (summaryEl) summaryEl.setAttribute('data-arbt-main-title', member.title);

      if (descEl) {
        var isSelf = member.isSelf || member.handle === self.handle;
        descEl.innerHTML = isSelf ? descSelfHtml : (member.descriptionHtml || '');
      }

      var state = (variant && variant.stockState) || member.stockState;
      var label = (variant && variant.stockLabel) || member.stockLabel;
      if (stockEl) {
        stockEl.setAttribute('data-state', state);
        stockEl.setAttribute('data-inventory-scope', member.stockScope);
        STATES.forEach(function (s) {
          stockEl.classList.toggle('arb-stock--' + s, s === state);
        });
        if (stockLbl) stockLbl.textContent = label;
      }

      if (skuEl) skuEl.textContent = (variant && variant.sku) || '';
      if (voltEl && variant && variant.voltage) voltEl.textContent = variant.voltage;

      var available = variant ? variant.available : member.available;
      setButton(addBtn, available, member.addLabel);
      setButton(barBtn, available, member.addLabel);

      /* The vertical SURFACE, not the vertical CONTENT: the breadcrumb's
         vertical link is the one server-rendered vertical element that is
         cheap and safe to re-point, using the map arb-main-product already
         put on the nav. The vertical metaobjects (clever bits, story panels)
         stay server-rendered for self — they are deliberately not in the
         embed, and re-rendering them would be the fetch PLAN §2 rejected. */
      sectionRoot.setAttribute && sectionRoot.setAttribute('data-arbt-family-vertical', member.contextVertical || '');
      if (breadcrumbVertical && breadcrumb) {
        var vertical = member.contextVertical;
        var text = breadcrumb.getAttribute('data-' + vertical + '-label');
        var href = breadcrumb.getAttribute('data-' + vertical + '-url');
        if (text && href) {
          breadcrumbVertical.textContent = text;
          breadcrumbVertical.setAttribute('href', href);
        } else if (breadcrumbVerticalInitial) {
          breadcrumbVertical.textContent = breadcrumbVerticalInitial.text;
          breadcrumbVertical.setAttribute('href', breadcrumbVerticalInitial.href);
        }
      }
    }

    function writeVariantId(variant) {
      if (!variant) return;
      var inputs = idInputs();
      inputs.forEach(function (input) {
        input.value = String(variant.id);
        /* Only when the record carries the numeric price — the add-ons
           subtotal re-bases off this attribute, and a blank would zero it.
           ⚠️ It is SET here, not merely refreshed (T10): arbt-option-picker is
           the only thing that renders the attribute, and it renders nothing on
           a market where this product has a single variant — which is the AU
           parent. Without it the order summary kept pricing SELF while a kit
           was on screen, so ticking an accessory showed the skin's base. The
           plain product-form hidden input now carries the ACTIVE member's
           variant price too, and the change dispatched below is what makes the
           summary re-compute. */
        if (typeof variant.price === 'number') {
          input.setAttribute('data-arb-variant-price', String(variant.price));
        }
      });
      /* Downstream consumers (the section's variant sync, the add-on media
         jump) listen for a bubbling change on name="id". They read SELF's
         blob, so for a kit they find no record and correctly do nothing —
         this file has already painted. Firing anyway keeps self's path
         byte-identical to a non-family PDP. */
      if (inputs.length) inputs[inputs.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
    }

    function apply(handle, announce) {
      var member = byHandle[handle];
      if (!member) return;
      activeHandle = member.handle;

      rebuildPicker(member);
      var variant = currentVariant(member);
      if (variant) activeVariantId[member.handle] = variant.id;

      /* A bundle's own parent variant id is what goes in the cart; Shopify
         expands it into its component lines server-side, so there is nothing
         special to post (PLAN §2 "Analytics/cart").
         ⚠️ This runs BEFORE paint(), and the order is load-bearing (T10): the
         change it dispatches now reaches the add-ons summary, whose update()
         rewrites [data-arb-price-display] with its own Intl.NumberFormat
         string ("A$1,799.00"). paint() must land after it so the money string
         Liquid produced with money_with_currency ("$1,799.00 AUD") is what
         stays on screen — otherwise a swap alone changed the price FORMAT.
         Everything both functions touch is written in the same task, so the
         browser only ever paints the final state. */
      writeVariantId(variant);
      paint(member, variant);
      showGalleryMedia(showGallery(member), variant);
      showVerticalBlock(member);
      showTabs(member);
      showExtras(member);
      swapMemberSections(member);

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', memberUrl(member));
      }
      if (announce && live) {
        var radio = group.querySelector('input[type="radio"][value="' + cssEscape(member.handle) + '"]');
        live.textContent = (radio && radio.getAttribute('data-arbt-family-announce')) || '';
      }
    }

    group.addEventListener('change', function (event) {
      var radio = event.target;
      if (!radio || radio.type !== 'radio' || !radio.hasAttribute('data-arbt-family-option')) return;
      apply(radio.value, true);
    });

    /* Pill changes now resolve against the ACTIVE member's variants. */
    if (picker) {
      picker.addEventListener('change', function (event) {
        var radio = event.target;
        if (!radio || radio.type !== 'radio' || !radio.hasAttribute('data-arbt-option-index')) return;
        var member = byHandle[activeHandle];
        if (!member) return;

        var selections = {};
        Array.prototype.slice.call(picker.querySelectorAll('input[type="radio"][data-arbt-option-index]:checked')).forEach(function (checked) {
          selections[checked.getAttribute('data-arbt-option-index')] = checked.value;
        });

        var best = null;
        var bestScore = -1;
        member.variants.forEach(function (v) {
          var score = 0;
          var indexes = Object.keys(selections);
          for (var i = 0; i < indexes.length; i++) {
            if (v.options[Number(indexes[i])] !== selections[indexes[i]]) return;
            score += 10;
          }
          if (v.available) score += 1;
          if (score > bestScore) {
            bestScore = score;
            best = v;
          }
        });
        if (!best) return;

        activeVariantId[member.handle] = best.id;
        Array.prototype.slice.call(picker.querySelectorAll('input[type="radio"][data-arbt-option-index]')).forEach(function (input) {
          input.checked = best.options[Number(input.getAttribute('data-arbt-option-index'))] === input.value;
        });
        writeVariantId(best);
        paint(member, best);
        jumpToVariantMedia(document.querySelector('[data-arb-gallery][data-arbt-family-member="' + cssEscape(member.handle) + '"]'), best);
      });
    }

    /* A Kits & Bundles tile for another family member is not a separate
       destination — it is this page showing a different product. The server
       renders its link as a real `?member=` URL on the current path (so with
       JS off it still lands on that member, and middle-click / cmd-click still
       open a page), and this listener turns a plain click into the swap the
       pills already do: check the pill, apply, announce, and put focus on the
       pill so the keyboard lands where the announcement points. Focus moving
       is deliberate and is the ONE place this file moves it — the shopper
       activated a link and would otherwise be left on a control that no longer
       describes the page. */
    sectionRoot.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var target = event.target;
      var link = target && target.closest ? target.closest('[data-arbt-family-member-link]') : null;
      if (!link) return;
      var handle = resolveMemberHandle(link.getAttribute('data-arbt-family-member-link'));
      if (!handle || !byHandle[handle] || handle === activeHandle) return;
      event.preventDefault();
      var pill = group.querySelector('input[type="radio"][value="' + cssEscape(handle) + '"]');
      if (pill) pill.checked = true;
      apply(handle, true);
      if (pill) pill.focus();
    });

    /* Preselect. ?member=<handle> is the current form. ?setup=kit|skin is the
       retired parent's deep link (PLAN §2 "URL"): `skin` is self, which is
       index 0 by construction, and `kit` is the first other member — read off
       the embed's order, never off a handle or a title. */
    var params = new URLSearchParams(window.location.search);
    var requested = params.get('member');
    if (!requested || !byHandle[requested]) {
      var setup = params.get('setup');
      if (setup === 'skin') requested = self.handle;
      else if (setup === 'kit' && members[1]) requested = members[1].handle;
      else requested = null;
    }
    if (requested && byHandle[requested] && requested !== self.handle) {
      var preselect = group.querySelector('input[type="radio"][value="' + cssEscape(resolveMemberHandle(requested)) + '"]');
      if (preselect) preselect.checked = true;
      /* No announcement on load: the region is bound empty on purpose. */
      apply(requested, false);
    }
  }

  /* Bind on DOMContentLoaded, not at execution. A defer script executes with
     readyState already 'interactive', i.e. BEFORE the DOMContentLoaded
     handlers the section's own inline scripts registered mid-parse — and one
     of those (the ?vertical= breadcrumb/description script in
     arb-main-product) caches the description's innerHTML as its "entry"
     state at that moment. Painting a member first would teach it the WRONG
     entry copy on a ?member= deep link. Registering here instead runs this
     file after every mid-parse listener, because DOMContentLoaded handlers
     fire in registration order. `complete` is the cached/bfcache re-entry,
     where the event has already gone by. */
  if (document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }
})();
